import { TaskId } from "@tento-lona/sheets";
import { PropertyList } from "@tento-ui/components/input/property-list";
import { Switch } from "@tento-ui/components/controls/switch";
import { Storybook } from "@tento-ui/ui/storybook";
import { Uuid } from "@tento-core/uuid";
import { DateTime, MsSinceEpoch, Utc } from "@tento-chrono";
import { TaskRowItem } from "@tento-lona/sheets-ui/platform";
import { TaskTable } from "./task-table";
import { TasksClient } from "@tento-lona";
import { Network } from "@tento-lona/network";

export class TaskTableStoryBook extends Storybook.Default {
  title(): string {
    return "Task Table";
  }

  description(): Option<string> {
    return `A virtualized table component for displaying and managing tasks.

**Features:**
- Card style and inline layout modes
- Status indicators (planned, in-progress, completed)
- Importance levels with visual hierarchy
- Due date display with overdue highlighting`;
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Task Table with Sample Data (Controls)",
        render: () => {
          const sampleTasks: TaskRowItem[] = [
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-1",
              displayNumber: 1,
              title: "Complete user authentication system",
              status: "inProgress",
              importance: "high",
              dueDate: DateTime.fromMse(Date.now() as MsSinceEpoch, Utc),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-2",
              displayNumber: 2,
              title: "Design login page mockups",
              status: "completed",
              importance: "medium",
              dueDate: DateTime.fromMse(
                (Date.now() + 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-3",
              displayNumber: 3,
              title: "Setup database schema",
              status: "planned",
              importance: "high",
              dueDate: DateTime.fromMse(
                (Date.now() - 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-4",
              displayNumber: 4,
              title: "Write unit tests for API endpoints",
              status: "autoScheduled",
              importance: "low",
              dueDate: DateTime.fromMse(
                (Date.now() + 86400000 * 7) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-5",
              displayNumber: 5,
              title: "Code review and documentation updates",
              status: "planned",
              importance: null,
              dueDate: null,
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
          ];

          const $table = TaskTable.make();
          const mock = makeMockClient(sampleTasks);
          $table.bind(mock, { expanded: false });

          const $expandedSwitch = Switch.make();
          $expandedSwitch.bind({
            checked: false,
            onChange: (on) => {
              $table.bind(mock, { expanded: on });
            },
          });

          const $cardStyleSwitch = Switch.make();
          $cardStyleSwitch.bind({
            checked: false,
            onChange: (on) => {
              $table.$root.tvm?.updateCardStyle(on ? "card" : "default");
            },
          });

          const $propertyList = PropertyList.makeWith({
            expanded: {
              label: "Expanded Mode",
              $e: $expandedSwitch,
              order: 0,
            },
            cardStyle: {
              label: "Card Style",
              $e: $cardStyleSwitch,
              order: 1,
            },
          });

          return {
            $content: $table.assignStyles({
              width: "100%",
              maxWidth: "800px",
            }),
            $controls: $propertyList,
          };
        },
      },
      {
        title: "Task Table with Sample Data",
        render: () => {
          const sampleTasks: TaskRowItem[] = [
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-1",
              displayNumber: 1,
              title: "Complete user authentication system",
              status: "inProgress",
              importance: "high",
              dueDate: DateTime.fromMse(Date.now() as MsSinceEpoch, Utc),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-2",
              displayNumber: 2,
              title: "Design login page mockups",
              status: "completed",
              importance: "medium",
              dueDate: DateTime.fromMse(
                (Date.now() + 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-3",
              displayNumber: 3,
              title: "Setup database schema",
              status: "planned",
              importance: "high",
              dueDate: DateTime.fromMse(
                (Date.now() - 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-4",
              displayNumber: 4,
              title: "Write unit tests for API endpoints",
              status: "autoScheduled",
              importance: "low",
              dueDate: DateTime.fromMse(
                (Date.now() + 86400000 * 7) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-5",
              displayNumber: 5,
              title: "Code review and documentation updates",
              status: "planned",
              importance: null,
              dueDate: null,
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
          ];

          const $table = TaskTable.make();
          $table.bind(makeMockClient(sampleTasks), { expanded: false });
          return $table.assignStyles({
            width: "100%",
            maxWidth: "800px",
          });
        },
      },
      {
        title: "Task Table Expanded Mode",
        render: () => {
          const sampleTasks: TaskRowItem[] = [
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-1",
              displayNumber: 1,
              title: "Complete user authentication system",
              status: "inProgress",
              importance: "high",
              dueDate: DateTime.fromMse(Date.now() as MsSinceEpoch, Utc),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-2",
              displayNumber: 2,
              title: "Design login page mockups",
              status: "completed",
              importance: "medium",
              dueDate: DateTime.fromMse(
                (Date.now() + 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-3",
              displayNumber: 3,
              title: "Setup database schema",
              status: "planned",
              importance: "high",
              dueDate: DateTime.fromMse(
                (Date.now() - 86400000) as MsSinceEpoch,
                Utc,
              ),
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
            TaskRowItem.of({
              id: TaskId.of(Uuid.make()),
              displayId: "T-4",
              displayNumber: 4,
              title: "Code review completed",
              status: "completed",
              importance: null,
              dueDate: null,
              completedAt: null,
              createdAt: null,
              description: null,
              eventId: null,
              position: null,
              scheduledEnd: null,
              scheduledStart: null,
            }),
          ];

          const $table = TaskTable.make();
          $table.bind(makeMockClient(sampleTasks), { expanded: true });
          return $table.assignStyles({
            width: "100%",
            maxWidth: "800px",
          });
        },
      },
      {
        title: "Empty State",
        render: () => {
          const $table = TaskTable.make();
          $table.bind(makeMockClient([]), { expanded: false });
          return $table.assignStyles({
            width: "100%",
            maxWidth: "800px",
          });
        },
      },
    ];
  }
}

function makeMockClient(tasks: TaskRowItem[]): TasksClient.Api {
  return {
    list: async () => tasks,
    get: async (id: string) => tasks.find((t) => t.id.toString() === id) ?? null,
    peek: (id: string) => tasks.find((t) => t.id.toString() === id) ?? null,
    create: async (attrs) => ({ id: Uuid.make(), ...attrs }) as Network.Task,
    update: async (task, attrs) => {
      const held = tasks.find((t) => t.id.toString() === task.id);
      if (held == null) throw new Error(`no task ${task.id}`);
      return { kind: "accepted", view: { ...TasksClient.merge(held, attrs), id: held.id }, source: "merged" };
    },
    delete: async () => {},
  };
}
